import React from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Delete, ExpandMore } from '@mui/icons-material';

import ColorPickerField from './ColorPickerField';
import { ChoiceOption } from './types';

interface ChoiceItemEditorProps {
  choiceKey: string;
  choice: ChoiceOption;
  onChange: (key: string, choice: ChoiceOption) => void;
  onDelete: (key: string) => void;
  canDelete: boolean;
}

const ChoiceItemEditor: React.FC<ChoiceItemEditorProps> = ({
  choiceKey,
  choice,
  onChange,
  onDelete,
  canDelete,
}) => {
  const handleFieldChange = (field: keyof ChoiceOption, value: string) => {
    onChange(choiceKey, { ...choice, [field]: value });
  };

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle2" color="textSecondary">
          Choice: {choiceKey}
        </Typography>
        {canDelete && (
          <IconButton size="small" onClick={() => onDelete(choiceKey)} color="error">
            <Delete />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Display Text"
            value={choice.text}
            onChange={(e) => handleFieldChange('text', e.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Value"
            value={choice.value}
            onChange={(e) => handleFieldChange('value', e.target.value)}
            fullWidth
            required
            helperText="The value stored in context"
          />
        </Grid>
      </Grid>

      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2">Styling Options (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Icon (Emoji)"
                value={choice.icon || ''}
                onChange={(e) => handleFieldChange('icon', e.target.value)}
                fullWidth
                placeholder="Enter an emoji (e.g., 🎉)"
                helperText="Copy and paste an emoji from your system emoji picker"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ColorPickerField
                label="Icon Color"
                value={choice.iconColor}
                onChange={(color) => handleFieldChange('iconColor', color)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ColorPickerField
                label="Background Color"
                value={choice.backgroundColor}
                onChange={(color) => handleFieldChange('backgroundColor', color)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ColorPickerField
                label="Text Color"
                value={choice.textColor}
                onChange={(color) => handleFieldChange('textColor', color)}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default ChoiceItemEditor;
